import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { SUB_SCORE_LABELS } from "../../lib/constants";

/**
 * Expandable panel that shows, per sub-score, the evidence found in the CV
 * + the AI's reasoning. Lets the recruiter understand WHY the AI gave a
 * given score, not just the number.
 */
export default function ScoreBreakdown({ detail = {}, subScores = {}, valuedSignals = [], candidateId, onRescored }) {
    const [open, setOpen] = useState(false);
    const [rescoring, setRescoring] = useState(false);
    const entries = Object.entries(SUB_SCORE_LABELS);
    const hasDetail = Object.values(detail || {}).some(
        (d) => (d?.evidence || "").trim().length > 0
            && !d.evidence.includes("CV non analysé")
            && !d.evidence.includes("heuristique"),
    );

    async function rescoreNow(e) {
        e.stopPropagation();
        if (!candidateId) return;
        setRescoring(true);
        try {
            await api.post(`/candidates/${candidateId}/rescore`);
            toast.success("Détail IA mis à jour");
            if (onRescored) onRescored();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors du re-score");
        } finally {
            setRescoring(false);
        }
    }

    return (
        <div className="card-soft p-6" data-testid="score-breakdown">
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="score-breakdown-toggle"
                className="w-full flex items-center justify-between gap-3 text-left"
            >
                <div className="min-w-0 flex-1">
                    <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                        <Sparkles className="h-3 w-3" /> Pourquoi ce score ?
                    </div>
                    <h3 className="mt-2 text-lg font-extrabold text-shariti-black">
                        Détail du calcul IA
                    </h3>
                    {hasDetail ? (
                        <p className="text-xs font-medium text-slate-500 mt-0.5">
                            Pour chaque dimension : l'évidence trouvée dans le CV et le raisonnement.
                        </p>
                    ) : (
                        <p className="text-xs font-medium text-slate-500 mt-0.5">
                            Ce candidat a été scoré avec une heuristique simple — re-scorez pour obtenir le détail IA explicite.
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {!hasDetail && candidateId && (
                        <button
                            type="button"
                            onClick={rescoreNow}
                            disabled={rescoring}
                            data-testid="score-breakdown-rescore"
                            className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white px-3 h-8 text-[11px] font-extrabold hover:bg-[#d66528] transition shadow-btn"
                        >
                            {rescoring ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            Re-scorer maintenant
                        </button>
                    )}
                    <div className="h-9 w-9 rounded-full bg-shariti-cream text-slate-500 inline-flex items-center justify-center shadow-sm">
                        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                </div>
            </button>

            {open && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-5 space-y-3 overflow-hidden"
                >
                    {valuedSignals.length > 0 && (
                        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-2">
                                ✨ Signaux Shariti détectés
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {valuedSignals.map((s, i) => (
                                    <span
                                        key={i}
                                        className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-white border border-emerald-300 text-emerald-700"
                                    >
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {entries.map(([key, label]) => {
                        const score = (detail[key]?.score) ?? subScores[key] ?? 0;
                        const ev = (detail[key]?.evidence || "").trim();
                        const reason = (detail[key]?.reasoning || "").trim();
                        const tone =
                            key === "risque_attrition"
                                ? (score >= 60 ? "text-shariti-red" : score >= 35 ? "text-amber-700" : "text-emerald-600")
                                : (score >= 75 ? "text-emerald-600" : score >= 55 ? "text-shariti-orange" : "text-shariti-red");
                        return (
                            <div
                                key={key}
                                className="rounded-2xl border border-shariti-gray p-4 hover:border-shariti-orange/40 transition"
                                data-testid={`breakdown-${key}`}
                            >
                                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                                    <div className="text-sm font-extrabold text-shariti-black">{label}</div>
                                    <div className={`text-xl font-black tabular-nums ${tone}`}>{score}<span className="text-[10px] font-bold text-slate-400 ml-0.5">/100</span></div>
                                </div>
                                {ev && (
                                    <div className="text-xs font-medium text-slate-600 mb-1 leading-snug">
                                        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mr-1.5">Évidence</span>
                                        {ev}
                                    </div>
                                )}
                                {reason && (
                                    <div className="text-xs font-semibold text-shariti-orange leading-snug">
                                        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mr-1.5">Raisonnement</span>
                                        {reason}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </motion.div>
            )}
        </div>
    );
}
